/* tool-shell.js: the shell every SongSuey data tool runs on (window.SongSueyToolShell).

   Contract
   --------
   mount({ root, config, schema, defaults, prefix, onChange, onAction, onExport,
           columns, noun, reveal })
     root      the #toolApp element (or the .tool element inside a layer). Its
               data-tool-config attribute carries the page's `tool` front matter;
               `config` overrides it and `schema` defaults to config.controls.
     defaults  extra defaults laid over the schema's own `default` values.
     prefix    null: state lives in the query string (a tool page).
               "map": state lives in the fragment as #map=<state> (a layer).
     onChange(state, changedKeys)  every change from a control, set(), reset()
               or a history move. Never called for a silent set.
     onAction(key, instance)  a control of kind "button" was clicked.
     onExport(kind)  after csv | json | share | reset ran.
   returns { state, set, get, serialize, reset, results, export, track,
             loadScripts, loadStyles, fetchJson, openLayer, closeLayer,
             readUrl, destroy, root, stage, panel, tool }
     set(key, value) or set({ k: v }), with an optional last argument
               { silent: true } for values an engine writes on settle (lat, lng,
               zoom) that must not echo back through onChange.
     serialize()  "view=flat&color=genre": non-default keys, in schema order.
     results(rows, { total, noun, columns, label, render, cap })  hands the shell
               the rows on screen. The count line, the export files and the
               optional list under [data-tool-results] all read them.
     export("csv" | "json")  downloads the current rows. CSV is RFC 4180 with a
               leading "# songsuey <tool> <date> <state>" line; JSON is
               { tool, generated, view, rows }.

   URL state
   ---------
   Read order at mount: schema defaults, then the root's data-tool-state JSON,
   then the URL. Writes go through history.replaceState, debounced 150 ms, so a
   range drag never floods the history. popstate re-reads the URL and reports
   the keys that moved through onChange.

   Layer
   -----
   openLayer({ id, config, state, fullPageUrl, launcher, onMount, onChange,
   onAction, onClose }) builds a native <dialog>, mounts a shell inside it with
   prefix = id, pushes a history entry carrying #<id>=<state> so the Back
   gesture closes it, and returns focus to the launcher on close. showModal()
   gives the focus trap, Escape and the backdrop for free. readLayerFragment()
   reports a #<id>=<state> fragment already in the address bar on page load, so
   a launcher can reopen the layer a shared link points at.

   Markup
   ------
   renderPanel(config) and renderShell(config, stageHtml) build the same markup
   src/_includes/tool-controls.njk and tool-export.njk render at build time.
   scripts/util/check-tool-shell-markup.js diffs the two in postbuild, so the
   page and the layer never drift apart. Change a tag in one, change both.

   Plain browser script, no bundler, same style as the rest of src/assets/js.
   Runs under Node's vm for the markup check, which is why nothing touches
   document at load time. */
(function (global) {
	"use strict";

	var URL_DEBOUNCE_MS = 150;
	var SEARCH_DEBOUNCE_MS = 200;
	var NOTE_MS = 1800;
	var RESULTS_CAP = 60;
	var SPRITE = "/assets/icons/sprite.svg#";
	// Order and wording of the export bar. Twin of tool-export.njk.
	var EXPORT_KINDS = [
		{ kind: "csv", icon: "fa-file-csv", label: "CSV", cls: "tool-btn", tip: "The rows on screen as a spreadsheet file" },
		{ kind: "json", icon: "fa-file-code", label: "JSON", cls: "tool-btn", tip: "The rows on screen as a JSON file" },
		{ kind: "share", icon: "fa-link", label: "Share link", cls: "tool-btn", tip: "Copy a link to this exact view" },
		{ kind: "reset", icon: "fa-rotate-left", label: "Reset", cls: "tool-btn tool-btn--quiet", tip: "Every control back to its default" }
	];
	var DEFAULT_EXPORTS = ["csv", "json", "share", "reset"];
	// The two wrappers the layout writes by hand in tool.njk. The markup check
	// asserts the layout still contains these exact strings.
	var STAGE_OPEN = '<div class="tool-stage">';
	var PANEL_OPEN = '<aside class="tool-panel" aria-label="';

	/* ---------- markup ---------- */

	// Same five characters Nunjucks autoescape rewrites, same entities.
	function esc(value) {
		return String(value == null ? "" : value)
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;");
	}
	function icon(name) {
		return '<svg class="icon" focusable="false" aria-hidden="true"><use href="' + SPRITE + esc(name) + '"></use></svg>';
	}
	function defaultString(control) {
		return control.default == null ? "" : String(control.default);
	}
	function hasCount(option) {
		return option.count != null;
	}

	// A control with a tip gets data-tip on its label (or on the toggle itself) and
	// a small info glyph, so every setting explains itself on hover or focus.
	var HINT_SVG = '<svg class="icon tool-control__hint" focusable="false" aria-hidden="true"><use href="/assets/icons/sprite.svg#fa-circle-info"></use></svg>';
	function tipAttr(control) { return control.tip ? ' data-tip="' + esc(control.tip) + '" data-tip-side="right"' : ""; }
	function tipHint(control) { return control.tip ? " " + HINT_SVG : ""; }
	function renderControl(control, prev, idPrefix) {
		if (control.kind === "hidden") return "";
		var key = esc(control.key);
		var id = esc(idPrefix + control.key);
		var def = defaultString(control);
		var defVals = def.split(",");
		var options = control.options || [];
		var html = "";
		var i, o, on;
		if (control.section && (!prev || prev.section !== control.section)) {
			html += '<h3 class="tool-controls__section">' + esc(control.section) + "</h3>";
		}
		html += '<div class="tool-control tool-control--' + esc(control.kind) + '" data-tool-field="' + key + '">';
		if (control.kind === "segmented" || control.kind === "chips") {
			var isChips = control.kind === "chips";
			html += '<span class="tool-control__label" id="' + id + '-label"' + tipAttr(control) + '>' + esc(control.label) + tipHint(control) + "</span>";
			html += '<div class="' + (isChips ? "tool-chips" : "tool-seg") + '" role="group" aria-labelledby="' + id + '-label" data-tool-control="' + key + '"' + (isChips && control.multiple ? " data-multiple" : "") + ">";
			for (i = 0; i < options.length; i++) {
				o = options[i];
				on = defVals.indexOf(String(o.value == null ? "" : o.value)) !== -1;
				html += '<button class="' + (isChips ? "tool-chip" : "tool-seg__btn") + '" type="button" data-value="' + esc(o.value) + '" aria-pressed="' + (on ? "true" : "false") + '">';
				if (!isChips && o.icon) html += icon(o.icon) + " ";
				html += esc(o.label);
				if (isChips && hasCount(o)) html += ' <span class="tool-chip__count">' + esc(o.count) + "</span>";
				html += "</button>";
			}
			html += "</div>";
		} else if (control.kind === "select") {
			html += '<label class="tool-control__label" for="' + id + '"' + tipAttr(control) + '>' + esc(control.label) + tipHint(control) + "</label>";
			html += '<select class="tool-select" id="' + id + '" data-tool-control="' + key + '">';
			for (i = 0; i < options.length; i++) {
				o = options[i];
				on = defVals.indexOf(String(o.value == null ? "" : o.value)) !== -1;
				html += '<option value="' + esc(o.value) + '"' + (on ? " selected" : "") + ">" + esc(o.label) + (hasCount(o) ? " (" + esc(o.count) + ")" : "") + "</option>";
			}
			html += "</select>";
		} else if (control.kind === "toggle") {
			html += '<button class="tool-toggle" type="button" role="switch" aria-checked="' + (def === "1" ? "true" : "false") + '" data-tool-control="' + key + '"' + tipAttr(control) + '><span class="tool-toggle__track" aria-hidden="true"><span class="tool-toggle__knob"></span></span><span class="tool-toggle__label">' + esc(control.label) + tipHint(control) + "</span></button>";
		} else if (control.kind === "range") {
			html += '<label class="tool-control__label" for="' + id + '"' + tipAttr(control) + '>' + esc(control.label) + tipHint(control) + ' <output class="tool-range__out" for="' + id + '">' + esc(def) + "</output></label>";
			html += '<input class="tool-range" type="range" id="' + id + '" min="' + esc(control.min) + '" max="' + esc(control.max) + '" step="' + esc(control.step == null ? 1 : control.step) + '" value="' + esc(def) + '" data-tool-control="' + key + '">';
		} else if (control.kind === "search") {
			html += '<label class="tool-control__label" for="' + id + '"' + tipAttr(control) + '>' + esc(control.label) + tipHint(control) + "</label>";
			html += '<div class="tool-search">' + icon("fa-magnifying-glass") + '<input class="tool-search__input" type="search" id="' + id + '" placeholder="' + esc(control.placeholder || "") + '" autocomplete="off" spellcheck="false" data-tool-control="' + key + '"></div>';
		} else if (control.kind === "button") {
			html += '<button class="tool-btn" type="button" data-tool-control="' + key + '" data-tool-kind="button">' + (control.icon ? icon(control.icon) + " " : "") + esc(control.label) + "</button>";
		}
		if (control.help) html += '<p class="tool-control__help">' + esc(control.help) + "</p>";
		html += "</div>";
		return html;
	}

	function renderControls(config, idPrefix) {
		var schema = config.controls || [];
		var html = '<div class="tool-controls" data-tool-controls>';
		for (var i = 0; i < schema.length; i++) {
			html += renderControl(schema[i], i > 0 ? schema[i - 1] : null, idPrefix);
		}
		return html + "</div>";
	}

	function renderExportBar(config) {
		var kinds = config.exports ? config.exports : DEFAULT_EXPORTS;
		var html = '<div class="tool-export" data-tool-export-bar><p class="tool-export__count" role="status" aria-live="polite" data-tool-count></p><div class="tool-export__actions">';
		for (var i = 0; i < EXPORT_KINDS.length; i++) {
			var e = EXPORT_KINDS[i];
			if (kinds.indexOf(e.kind) === -1) continue;
			html += '<button class="' + e.cls + '" type="button" data-tool-export="' + e.kind + '" data-tip="' + esc(e.tip) + '" data-tip-side="right">' + icon(e.icon) + " " + e.label + "</button>";
		}
		return html + "</div></div>";
	}

	function renderPanel(config, opts) {
		var idPrefix = (opts && opts.idPrefix) || (config.id + "-ctl-");
		return renderControls(config, idPrefix) + renderExportBar(config);
	}

	function renderShell(config, stageHtml, opts) {
		return STAGE_OPEN + (stageHtml || "") + "</div>" + PANEL_OPEN + esc(config.name) + ' controls">' + renderPanel(config, opts) + "</aside>";
	}

	/* ---------- state ---------- */

	function toStr(value) {
		if (value == null) return "";
		if (value === true) return "1";
		if (value === false) return "0";
		return String(value);
	}
	function has(obj, key) {
		return Object.prototype.hasOwnProperty.call(obj, key);
	}
	function schemaDefaults(schema, extra) {
		var d = {};
		for (var i = 0; i < schema.length; i++) d[schema[i].key] = defaultString(schema[i]);
		if (extra) for (var k in extra) if (has(extra, k) && has(d, k)) d[k] = toStr(extra[k]);
		return d;
	}
	// Copies the schema keys present in `source` onto `state`; returns the keys that moved.
	function applyState(state, schema, source) {
		var changed = [];
		if (!source) return changed;
		for (var i = 0; i < schema.length; i++) {
			var key = schema[i].key;
			if (!has(source, key)) continue;
			var next = toStr(source[key]);
			if (state[key] === next) continue;
			state[key] = next;
			changed.push(key);
		}
		return changed;
	}
	function parseQuery(qs) {
		var out = {};
		if (!qs) return out;
		var parts = qs.replace(/^[?#]/, "").split("&");
		for (var i = 0; i < parts.length; i++) {
			if (!parts[i]) continue;
			var at = parts[i].indexOf("=");
			var k = at === -1 ? parts[i] : parts[i].slice(0, at);
			var v = at === -1 ? "" : parts[i].slice(at + 1);
			try { out[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, " ")); } catch (e) { /* a mangled link is not a crash */ }
		}
		return out;
	}
	// Keeps the characters the map keys lean on readable: focus=place:united-states/georgia,genre=pop,rock.
	function encodeValue(value) {
		return encodeURIComponent(value).replace(/%2C/gi, ",").replace(/%3A/gi, ":").replace(/%2F/gi, "/");
	}
	function serializeState(schema, state, defaults) {
		var parts = [];
		for (var i = 0; i < schema.length; i++) {
			var key = schema[i].key;
			if (state[key] === defaults[key]) continue;
			parts.push(encodeURIComponent(key) + "=" + encodeValue(state[key]));
		}
		return parts.join("&");
	}
	function readJsonAttr(el, name) {
		try { return JSON.parse(el.getAttribute(name) || "null"); } catch (e) { return null; }
	}
	function cssQuote(value) {
		return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
	}

	/* ---------- loaders, analytics, download ---------- */

	var scriptCache = {};
	var styleCache = {};
	var jsonCache = {};

	// Scripts are inserted at once with async=false, so they download in parallel
	// and run in the order given (d3-array before d3-geo before topojson-client).
	function loadScript(url) {
		if (scriptCache[url]) return scriptCache[url];
		scriptCache[url] = new Promise(function (resolve, reject) {
			var s = document.createElement("script");
			s.async = false;
			s.src = url;
			s.onload = function () { resolve(url); };
			s.onerror = function () {
				delete scriptCache[url];
				reject(new Error("tool-shell: could not load " + url));
			};
			document.head.appendChild(s);
		});
		return scriptCache[url];
	}
	function loadScripts(urls) {
		if (typeof urls === "string") urls = [urls];
		var pending = [];
		for (var i = 0; i < urls.length; i++) pending.push(loadScript(urls[i]));
		return Promise.all(pending);
	}
	function loadStyle(url) {
		if (styleCache[url]) return styleCache[url];
		styleCache[url] = new Promise(function (resolve, reject) {
			var l = document.createElement("link");
			l.rel = "stylesheet";
			l.href = url;
			l.onload = function () { resolve(url); };
			l.onerror = function () {
				delete styleCache[url];
				reject(new Error("tool-shell: could not load " + url));
			};
			document.head.appendChild(l);
		});
		return styleCache[url];
	}
	function loadStyles(urls) {
		if (typeof urls === "string") urls = [urls];
		var pending = [];
		for (var i = 0; i < urls.length; i++) pending.push(loadStyle(urls[i]));
		return Promise.all(pending);
	}
	function fetchJson(url) {
		if (jsonCache[url]) return jsonCache[url];
		jsonCache[url] = global.fetch(url, { credentials: "same-origin" }).then(function (res) {
			if (!res.ok) throw new Error("tool-shell: " + url + " answered " + res.status);
			return res.json();
		});
		jsonCache[url].then(null, function () { delete jsonCache[url]; });
		return jsonCache[url];
	}

	// analytics.js ships on production builds only, so on a dev build gtag is
	// absent and this does nothing instead of throwing. Same helper as compare.js.
	function track(name, params) {
		if (typeof global.gtag === "function") global.gtag("event", name, params);
	}

	function download(filename, mime, text) {
		var blob = new Blob([text], { type: mime });
		var url = URL.createObjectURL(blob);
		var a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
	}
	function isoDate() {
		return new Date().toISOString().slice(0, 10);
	}
	// RFC 4180: a field holding a quote, a comma or a line break is quoted, quotes
	// inside it are doubled, records end in CRLF. Arrays join on "; ".
	function csvCell(value) {
		var s;
		if (value == null) s = "";
		else if (Object.prototype.toString.call(value) === "[object Array]") s = value.join("; ");
		else s = String(value);
		if (/[",\r\n]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
		return s;
	}
	function toCsv(rows, columns, headerLine) {
		var lines = [headerLine, columns.map(csvCell).join(",")];
		for (var i = 0; i < rows.length; i++) {
			var row = rows[i];
			var cells = [];
			for (var c = 0; c < columns.length; c++) cells.push(csvCell(row[columns[c]]));
			lines.push(cells.join(","));
		}
		return lines.join("\r\n") + "\r\n";
	}

	/* ---------- mount ---------- */

	// Label tips for icon-only buttons. Any element with data-tip shows its text on
	// hover and on keyboard focus (never on touch) in ONE floating element appended to
	// the page, or to the open dialog so it stays in the top layer. Positioned from the
	// button's rectangle, so it escapes the stage and the panel, whose overflow would
	// clip anything drawn inside them. data-tip-side="left" | "right" picks the side;
	// the default is away from the nearer viewport edge, and above when neither fits.
	var labelTipsBound = false, labelTipEl = null, labelTipHolder = null;
	function labelTipTarget(node) {
		return node && node.closest ? node.closest("[data-tip]") : null;
	}
	function showLabelTip(el) {
		var text = el.getAttribute("data-tip");
		if (!text) return;
		var holder = el.closest("dialog") || document.body;
		if (!labelTipEl || labelTipHolder !== holder) {
			if (labelTipEl && labelTipEl.parentNode) labelTipEl.parentNode.removeChild(labelTipEl);
			labelTipEl = document.createElement("div");
			labelTipEl.className = "tool-label-tip";
			labelTipEl.setAttribute("aria-hidden", "true");
			holder.appendChild(labelTipEl);
			labelTipHolder = holder;
		}
		labelTipEl.textContent = text;
		labelTipEl.classList.add("is-on");
		var r = el.getBoundingClientRect();
		var w = labelTipEl.offsetWidth, h = labelTipEl.offsetHeight, gap = 8;
		var side = el.getAttribute("data-tip-side") || (r.left + r.width / 2 > window.innerWidth / 2 ? "left" : "right");
		var x = side === "left" ? r.left - w - gap : r.right + gap;
		var y = r.top + r.height / 2 - h / 2;
		if (x < 4 || x + w > window.innerWidth - 4) {
			x = Math.max(4, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 4));
			y = r.top - h - gap;
			if (y < 4) y = r.bottom + gap;
		}
		y = Math.max(4, Math.min(y, window.innerHeight - h - 4));
		// Inside a dialog the box is the dialog's own; on the page it is the document.
		var base = holder === document.body ? { left: -window.scrollX, top: -window.scrollY } : holder.getBoundingClientRect();
		labelTipEl.style.left = Math.round(x - base.left) + "px";
		labelTipEl.style.top = Math.round(y - base.top) + "px";
	}
	function hideLabelTip() {
		if (labelTipEl) labelTipEl.classList.remove("is-on");
	}
	function bindLabelTips() {
		if (labelTipsBound || typeof document === "undefined") return;
		labelTipsBound = true;
		document.addEventListener("pointerover", function (e) {
			var el = labelTipTarget(e.target);
			if (el && e.pointerType !== "touch") showLabelTip(el);
		});
		document.addEventListener("pointerout", function (e) {
			if (labelTipTarget(e.target)) hideLabelTip();
		});
		document.addEventListener("focusin", function (e) {
			var el = labelTipTarget(e.target);
			if (el) showLabelTip(el);
		});
		document.addEventListener("focusout", hideLabelTip);
		document.addEventListener("pointerdown", hideLabelTip, true);
		document.addEventListener("keydown", function (e) { if (e.key === "Escape") hideLabelTip(); }, true);
		window.addEventListener("scroll", hideLabelTip, true);
		window.addEventListener("resize", hideLabelTip);
	}

	function mount(opts) {
		opts = opts || {};
		bindLabelTips();
		var root = opts.root || document.getElementById("toolApp");
		if (!root) throw new Error("tool-shell: mount needs a root element");
		var config = opts.config || readJsonAttr(root, "data-tool-config") || {};
		var schema = opts.schema || config.controls || [];
		var toolId = opts.tool || config.id || root.getAttribute("data-tool") || "tool";
		var prefix = opts.prefix || null;
		var noun = opts.noun || config.rowNoun || "rows";
		var defaults = schemaDefaults(schema, opts.defaults);
		var state = {};
		var key;
		for (key in defaults) if (has(defaults, key)) state[key] = defaults[key];
		applyState(state, schema, readJsonAttr(root, "data-tool-state"));
		applyState(state, schema, parseQuery(readLocation()));

		var stage = root.querySelector(".tool-stage");
		var panel = root.querySelector(".tool-panel");
		var countEl = root.querySelector("[data-tool-count]");
		var resultsEl = root.querySelector("[data-tool-results]");
		var rows = [];
		var columns = opts.columns || config.columns || null;
		var countText = "";
		var urlTimer = null;
		var searchTimer = null;
		var noteTimer = null;
		var destroyed = false;

		function readLocation() {
			if (!prefix) return global.location.search.slice(1);
			var marker = "#" + prefix + "=";
			var h = global.location.hash;
			return h.indexOf(marker) === 0 ? h.slice(marker.length) : "";
		}
		function serialize() {
			return serializeState(schema, state, defaults);
		}
		function stateCopy() {
			var copy = {};
			for (var k in state) if (has(state, k)) copy[k] = state[k];
			return copy;
		}
		function buildUrl() {
			var loc = global.location;
			var qs = serialize();
			if (!prefix) return loc.pathname + (qs ? "?" + qs : "") + loc.hash;
			return loc.pathname + loc.search + "#" + prefix + "=" + qs;
		}
		function writeUrl() {
			urlTimer = null;
			if (destroyed) return;
			var url = buildUrl();
			var loc = global.location;
			if (url !== loc.pathname + loc.search + loc.hash) {
				try { global.history.replaceState(global.history.state, "", url); } catch (e) { /* file: URLs and the like */ }
			}
			if (opts.onUrl) opts.onUrl(serialize(), url);
		}
		function scheduleUrl() {
			if (urlTimer) clearTimeout(urlTimer);
			urlTimer = setTimeout(writeUrl, URL_DEBOUNCE_MS);
		}
		function flushUrl() {
			if (!urlTimer) return;
			clearTimeout(urlTimer);
			writeUrl();
		}

		function controlEl(k) {
			return root.querySelector('[data-tool-control="' + cssQuote(k) + '"]');
		}
		function syncControls(keys) {
			for (var i = 0; i < schema.length; i++) {
				var c = schema[i];
				if (c.kind === "hidden") continue;
				if (keys && keys.indexOf(c.key) === -1) continue;
				var el = controlEl(c.key);
				if (!el) continue;
				var v = state[c.key];
				if (c.kind === "segmented" || c.kind === "chips") {
					var vals = v.split(",");
					var btns = el.querySelectorAll("[data-value]");
					for (var j = 0; j < btns.length; j++) {
						btns[j].setAttribute("aria-pressed", vals.indexOf(btns[j].getAttribute("data-value")) !== -1 ? "true" : "false");
					}
				} else if (c.kind === "select") {
					if (el.value !== v) el.value = v;
				} else if (c.kind === "toggle") {
					el.setAttribute("aria-checked", v === "1" ? "true" : "false");
				} else if (c.kind === "range") {
					if (el.value !== v) el.value = v;
					var out = root.querySelector('output[for="' + cssQuote(el.id) + '"]');
					if (out) out.textContent = v;
				} else if (c.kind === "search") {
					if (el.value !== v && document.activeElement !== el) el.value = v;
				}
			}
		}

		function set(keyOrMap, value, options) {
			var patch;
			if (typeof keyOrMap === "string") {
				patch = {};
				patch[keyOrMap] = value;
			} else {
				patch = keyOrMap || {};
				options = value;
			}
			options = options || {};
			var changed = [];
			for (var k in patch) {
				if (!has(patch, k) || !has(defaults, k)) continue;
				var next = toStr(patch[k]);
				if (state[k] === next) continue;
				state[k] = next;
				changed.push(k);
			}
			if (!changed.length) return changed;
			syncControls(changed);
			scheduleUrl();
			if (!options.silent && opts.onChange) opts.onChange(state, changed);
			return changed;
		}
		function get(k) {
			return state[k];
		}
		function reset() {
			var patch = {};
			for (var k in defaults) if (has(defaults, k)) patch[k] = defaults[k];
			var changed = set(patch);
			flushUrl();
			return changed;
		}
		// Re-reads the URL after a history move. A key missing from the URL means
		// its default, so Back out of a filtered view really clears the filter.
		function readUrl() {
			var incoming = parseQuery(readLocation());
			var changed = [];
			for (var k in defaults) {
				if (!has(defaults, k)) continue;
				var next = has(incoming, k) ? toStr(incoming[k]) : defaults[k];
				if (state[k] === next) continue;
				state[k] = next;
				changed.push(k);
			}
			if (!changed.length) return changed;
			syncControls(changed);
			if (opts.onChange) opts.onChange(state, changed);
			return changed;
		}

		/* results and export */

		function note(text) {
			if (!countEl) return;
			countEl.textContent = text;
			if (noteTimer) clearTimeout(noteTimer);
			noteTimer = setTimeout(function () {
				noteTimer = null;
				countEl.textContent = countText;
			}, NOTE_MS);
		}
		function renderResults(list, cap) {
			var html = "";
			for (var i = 0; i < list.length && i < cap; i++) {
				var r = list[i];
				var name = esc(r.name || r.id || "");
				html += '<li class="tool-result">';
				html += r.url ? '<a class="tool-result__name" href="' + esc(r.url) + '">' + name + "</a>" : '<span class="tool-result__name">' + name + "</span>";
				if (r.meta) html += '<span class="tool-result__meta">' + esc(r.meta) + "</span>";
				if (r.score != null) html += '<span class="score-badge score-' + esc(r.cls || "none") + '">' + esc(r.score) + "%</span>";
				html += "</li>";
			}
			if (list.length > cap) html += '<li class="tool-result tool-result--more">' + (list.length - cap) + " more in the export</li>";
			if (!list.length) html += '<li class="tool-result tool-result--empty">Nothing matches these filters. Reset to start over.</li>';
			resultsEl.innerHTML = html;
		}
		function results(nextRows, meta) {
			meta = meta || {};
			rows = nextRows || [];
			// A layer fills its stage after mount, so the list is found late on purpose.
			if (!resultsEl) resultsEl = root.querySelector("[data-tool-results]");
			if (meta.columns) columns = meta.columns;
			var n = rows.length;
			var word = meta.noun || noun;
			countText = meta.label || (meta.total != null && meta.total !== n ? n + " of " + meta.total + " " + word : n + " " + word);
			if (countEl && !noteTimer) countEl.textContent = countText;
			if (resultsEl && meta.render !== false) renderResults(rows, meta.cap || config.resultsCap || RESULTS_CAP);
			return rows;
		}
		function exportRows(kind) {
			var date = isoDate();
			var cols = columns || (rows.length ? Object.keys(rows[0]) : []);
			var view = serialize();
			if (kind === "csv") {
				download("songsuey-" + toolId + "-" + date + ".csv", "text/csv;charset=utf-8", toCsv(rows, cols, "# songsuey " + toolId + " " + date + " " + (view || "defaults")));
			} else {
				download("songsuey-" + toolId + "-" + date + ".json", "application/json;charset=utf-8", JSON.stringify({ tool: toolId, generated: new Date().toISOString(), view: stateCopy(), rows: rows }, null, "\t"));
			}
			track(toolId + "_export", { export_format: kind, tool: toolId });
		}
		function absolute(u) {
			try { return new URL(u, global.location.href).href; } catch (e) { return u; }
		}
		function shareUrl() {
			flushUrl();
			if (!prefix) return global.location.href;
			var base = opts.fullPageUrl || config.url || global.location.pathname;
			var qs = serialize();
			return absolute(base) + (qs ? "?" + qs : "");
		}
		function share() {
			var url = shareUrl();
			function done() {
				note("Link copied");
				track(toolId + "_share_copy", { tool: toolId });
			}
			function fallback() {
				try { global.prompt("Copy this link", url); } catch (e) { /* nothing left to try */ }
			}
			var nav = global.navigator;
			if (nav && nav.clipboard && nav.clipboard.writeText) nav.clipboard.writeText(url).then(done, fallback);
			else fallback();
		}
		function exportAction(kind) {
			if (kind === "csv" || kind === "json") exportRows(kind);
			else if (kind === "share") share();
			else if (kind === "reset") reset();
			else return;
			if (opts.onExport) opts.onExport(kind);
		}

		/* control binding, delegated on the root so a re-rendered panel keeps working */

		function onClick(e) {
			var t = e.target;
			if (!t || !t.closest) return;
			var exp = t.closest("[data-tool-export]");
			if (exp) {
				e.preventDefault();
				exportAction(exp.getAttribute("data-tool-export"));
				return;
			}
			var action = t.closest('[data-tool-kind="button"]');
			if (action) {
				e.preventDefault();
				if (opts.onAction) opts.onAction(action.getAttribute("data-tool-control"), api);
				return;
			}
			var toggle = t.closest(".tool-toggle[data-tool-control]");
			if (toggle) {
				e.preventDefault();
				var tk = toggle.getAttribute("data-tool-control");
				set(tk, state[tk] === "1" ? "0" : "1");
				return;
			}
			var opt = t.closest(".tool-seg__btn, .tool-chip");
			if (!opt) return;
			var group = opt.closest("[data-tool-control]");
			if (!group) return;
			e.preventDefault();
			var gk = group.getAttribute("data-tool-control");
			var val = opt.getAttribute("data-value") || "";
			if (group.hasAttribute("data-multiple")) {
				var list = state[gk] ? state[gk].split(",") : [];
				var at = list.indexOf(val);
				if (at === -1) list.push(val); else list.splice(at, 1);
				set(gk, list.join(","));
			} else if (state[gk] === val && opt.classList.contains("tool-chip") && val !== defaults[gk]) {
				// A second click on the active chip is "off", back to the default.
				set(gk, defaults[gk]);
			} else {
				set(gk, val);
			}
		}
		function onChange(e) {
			var el = e.target;
			if (!el || !el.hasAttribute || !el.hasAttribute("data-tool-control")) return;
			if (el.tagName === "SELECT") set(el.getAttribute("data-tool-control"), el.value);
		}
		function onInput(e) {
			var el = e.target;
			if (!el || !el.hasAttribute || !el.hasAttribute("data-tool-control")) return;
			var k = el.getAttribute("data-tool-control");
			if (el.type === "range") {
				var out = root.querySelector('output[for="' + cssQuote(el.id) + '"]');
				if (out) out.textContent = el.value;
				set(k, el.value);
			} else if (el.type === "search" || el.type === "text") {
				if (searchTimer) clearTimeout(searchTimer);
				searchTimer = setTimeout(function () {
					searchTimer = null;
					set(k, el.value);
				}, SEARCH_DEBOUNCE_MS);
			}
		}
		function onKeydown(e) {
			if (e.key !== "Enter") return;
			var el = e.target;
			if (!el || el.type !== "search" || !el.hasAttribute || !el.hasAttribute("data-tool-control")) return;
			if (searchTimer) {
				clearTimeout(searchTimer);
				searchTimer = null;
			}
			set(el.getAttribute("data-tool-control"), el.value);
		}
		function onPop() {
			readUrl();
		}

		root.addEventListener("click", onClick);
		root.addEventListener("change", onChange);
		root.addEventListener("input", onInput);
		root.addEventListener("keydown", onKeydown);
		if (!prefix) global.addEventListener("popstate", onPop);

		function destroy() {
			destroyed = true;
			root.removeEventListener("click", onClick);
			root.removeEventListener("change", onChange);
			root.removeEventListener("input", onInput);
			root.removeEventListener("keydown", onKeydown);
			if (!prefix) global.removeEventListener("popstate", onPop);
			if (urlTimer) clearTimeout(urlTimer);
			if (searchTimer) clearTimeout(searchTimer);
			if (noteTimer) clearTimeout(noteTimer);
		}

		syncControls();
		if (opts.reveal !== false) root.removeAttribute("hidden");

		var api = {
			tool: toolId,
			root: root,
			stage: stage,
			panel: panel,
			state: state,
			defaults: defaults,
			schema: schema,
			config: config,
			set: set,
			get: get,
			serialize: serialize,
			reset: reset,
			readUrl: readUrl,
			results: results,
			"export": exportAction,
			track: track,
			loadScripts: loadScripts,
			loadStyles: loadStyles,
			fetchJson: fetchJson,
			openLayer: openLayer,
			closeLayer: closeLayer,
			destroy: destroy
		};
		return api;
	}

	/* ---------- layer ---------- */

	var layers = {};

	function openLayer(o) {
		o = o || {};
		var config = o.config || {};
		var id = o.id || config.id || "tool";
		if (layers[id]) return layers[id].api;
		var fullPage = o.fullPageUrl || config.url || "";
		if (typeof global.HTMLDialogElement === "undefined") {
			// No <dialog>: the full page is the same tool with the same state.
			if (fullPage) global.location.href = fullPage;
			return null;
		}
		var schema = config.controls || [];
		var name = o.name || config.name || "";
		var launcher = o.launcher || document.activeElement;
		var marker = "#" + id + "=";
		var hadFragment = global.location.hash.indexOf(marker) === 0;
		var pushed = false;
		var closed = false;

		var dialog = document.createElement("dialog");
		dialog.className = "tool-layer";
		dialog.setAttribute("aria-labelledby", id + "-layer-title");
		dialog.innerHTML =
			'<div class="tool-layer__head">' +
				'<h2 class="tool-layer__title" id="' + esc(id) + '-layer-title">' + esc(name) + "</h2>" +
				'<div class="tool-layer__tools">' +
					(fullPage ? '<a class="tool-layer__full" href="' + esc(fullPage) + '" aria-label="Open the full page"><span>Open the full page</span> ' + icon("fa-arrow-up-right-from-square") + "</a>" : "") +
					// autofocus: showModal() lands the reader on Close, not on the
					// "Open the full page" link, so an Enter pressed to acknowledge the
					// new dialog does not leave the page.
					'<button class="tool-layer__close" type="button" aria-label="Close" data-tip="Close (Esc)" data-tip-side="left" autofocus>' + icon("fa-xmark") + "</button>" +
				"</div>" +
			"</div>" +
			'<div class="tool-layer__body"><div class="tool tool--layer" data-tool="' + esc(id) + '">' + renderShell(config, "", { idPrefix: id + "-layer-ctl-" }) + "</div></div>";
		var shellRoot = dialog.querySelector(".tool");
		if (o.state) shellRoot.setAttribute("data-tool-state", JSON.stringify(o.state));
		document.body.appendChild(dialog);
		document.documentElement.classList.add("tool-layer-open");

		// One history entry per open, so Back closes the layer and the fragment is
		// shareable. A page that loads with the fragment already there reuses the
		// entry it is on: Back then leaves the page, which is what the reader expects.
		if (!hadFragment) {
			var defaults = schemaDefaults(schema);
			var initial = {};
			for (var k in defaults) if (has(defaults, k)) initial[k] = defaults[k];
			applyState(initial, schema, o.state);
			try {
				global.history.pushState({ toolLayer: id }, "", global.location.pathname + global.location.search + marker + serializeState(schema, initial, defaults));
				pushed = true;
			} catch (e) { /* no history API: the layer still opens, Escape still closes */ }
		} else if (!(global.history.state && global.history.state.toolLayer === id)) {
			try { global.history.replaceState({ toolLayer: id }, "", global.location.href); } catch (e) { /* same */ }
		}

		var fullLink = dialog.querySelector(".tool-layer__full");
		function setFullLink(qs) {
			if (fullLink) fullLink.href = fullPage + (qs ? "?" + qs : "");
		}
		var instance = mount({
			root: shellRoot,
			config: config,
			schema: schema,
			prefix: id,
			tool: id,
			fullPageUrl: fullPage,
			noun: o.noun,
			columns: o.columns,
			onChange: o.onChange,
			onAction: o.onAction,
			onExport: o.onExport,
			onUrl: setFullLink,
			reveal: false
		});
		setFullLink(instance.serialize());

		function finish(reason) {
			if (closed) return;
			closed = true;
			instance.destroy();
			global.removeEventListener("popstate", onPop);
			if (dialog.open) dialog.close();
			if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
			document.documentElement.classList.remove("tool-layer-open");
			delete layers[id];
			if (global.location.hash.indexOf(marker) === 0) {
				try { global.history.replaceState(null, "", global.location.pathname + global.location.search); } catch (e) { /* same */ }
			}
			if (launcher && launcher.focus) {
				try { launcher.focus(); } catch (e) { /* launcher gone */ }
			}
			if (o.onClose) o.onClose(reason);
		}
		function close() {
			if (closed) return;
			if (pushed && global.history.state && global.history.state.toolLayer === id) {
				// Our entry is on top: step back and let popstate finish the close, so
				// Escape, the Close button and the Back gesture all leave the same history.
				global.history.back();
				return;
			}
			finish("close");
		}
		function onPop() {
			if (global.location.hash.indexOf(marker) === 0) {
				instance.readUrl();
				return;
			}
			finish("back");
		}
		global.addEventListener("popstate", onPop);
		dialog.addEventListener("cancel", function (e) {
			e.preventDefault();
			close();
		});
		dialog.addEventListener("close", function () {
			// The browser closed it on its own (a second Escape some browsers refuse to
			// cancel): keep the history honest and tidy up.
			if (closed) return;
			if (pushed && global.history.state && global.history.state.toolLayer === id) {
				try { global.history.back(); } catch (e) { /* same */ }
			}
			finish("close");
		});
		// The layer fills the viewport, so the "backdrop" a reader can click is the
		// gutter around the shell: the dialog itself, its body, or the .tool grid's
		// own padding. A click on any control or the stage lands on a child instead.
		dialog.addEventListener("click", function (e) {
			if (e.target === dialog || e.target === dialog.querySelector(".tool-layer__body") || e.target === shellRoot) close();
		});
		dialog.querySelector(".tool-layer__close").addEventListener("click", close);

		var api = {
			id: id,
			dialog: dialog,
			shell: instance,
			stage: shellRoot.querySelector(".tool-stage"),
			panel: shellRoot.querySelector(".tool-panel"),
			close: close
		};
		layers[id] = { api: api, close: close };
		bindLabelTips();
		dialog.showModal();
		if (o.onMount) o.onMount(instance, api.stage, api);
		return api;
	}

	function closeLayer(id) {
		var entry;
		if (id) entry = layers[id];
		else for (var k in layers) if (has(layers, k)) { entry = layers[k]; break; }
		if (entry) entry.close();
	}

	// { id, query } when the address bar already carries #<id>=<state>, else null.
	function readLayerFragment() {
		var m = /^#([a-z0-9-]+)=(.*)$/i.exec(global.location.hash || "");
		return m ? { id: m[1], query: m[2] } : null;
	}

	global.SongSueyToolShell = {
		mount: mount,
		bindLabelTips: bindLabelTips,
		openLayer: openLayer,
		closeLayer: closeLayer,
		readLayerFragment: readLayerFragment,
		renderPanel: renderPanel,
		renderShell: renderShell,
		renderControls: renderControls,
		renderExportBar: renderExportBar,
		serializeState: serializeState,
		parseQuery: parseQuery,
		schemaDefaults: schemaDefaults,
		loadScripts: loadScripts,
		loadStyles: loadStyles,
		fetchJson: fetchJson,
		track: track,
		STAGE_OPEN: STAGE_OPEN,
		PANEL_OPEN: PANEL_OPEN,
		EXPORT_KINDS: EXPORT_KINDS
	};
})(typeof window !== "undefined" ? window : this);
